import {
  getUrlCollectionComparableUrl,
  getUrlCollectionItemKey,
  getUrlCollectionPagePath,
  parseUrlCollectionData,
  reindexUrlCollectionItems,
} from '@home/utils/urlCollectionData';

import type { IUrlCollectionItem } from '@home/types/urlCollection';

export const SHORTCUT_NAME_MAX_LENGTH = 80;

export const getShortcutPagePath = (item: IUrlCollectionItem): string => {
  return parseUrlCollectionData(item.data).page_path || getUrlCollectionPagePath(item.url);
};

export const getShortcutTargetUrl = (item: IUrlCollectionItem): string => {
  return item.url || getShortcutPagePath(item);
};

export const getShortcutItemUrlKey = (item: IUrlCollectionItem): string => {
  return getUrlCollectionComparableUrl(getShortcutTargetUrl(item));
};

export const reorderShortcutItems = (
  items: IUrlCollectionItem[],
  sourceKey: string,
  targetKey: string,
): IUrlCollectionItem[] => {
  const sourceIndex = items.findIndex((item) => getUrlCollectionItemKey(item) === sourceKey);
  const targetIndex = items.findIndex((item) => getUrlCollectionItemKey(item) === targetKey);

  if (sourceIndex === -1 || targetIndex === -1 || sourceIndex === targetIndex) {
    return items;
  }

  const nextItems = [...items];
  const [movedItem] = nextItems.splice(sourceIndex, 1);
  nextItems.splice(targetIndex, 0, movedItem);

  return reindexUrlCollectionItems(nextItems);
};

export const hasShortcutItemsChanges = (
  sourceItems: IUrlCollectionItem[],
  targetItems: IUrlCollectionItem[],
): boolean => {
  if (sourceItems.length !== targetItems.length) {
    return true;
  }

  return sourceItems.some((item, index) => {
    const targetItem = targetItems[index];

    if (!targetItem) {
      return true;
    }

    return (
      item.id !== targetItem.id ||
      item.name !== targetItem.name ||
      item.order !== targetItem.order ||
      item.url !== targetItem.url ||
      item.system !== targetItem.system ||
      item.cover !== targetItem.cover ||
      item.entity_type !== targetItem.entity_type ||
      item.entity_id !== targetItem.entity_id ||
      item.data !== targetItem.data
    );
  });
};

export const applyPendingShortcutRename = (
  items: IUrlCollectionItem[],
  editingKey: string,
  editingName: string,
): IUrlCollectionItem[] => {
  if (!editingKey) {
    return items;
  }

  const nextName = editingName.trim();

  return items.map((item) => {
    if (getUrlCollectionItemKey(item) !== editingKey) {
      return item;
    }

    return {
      ...item,
      name: nextName || item.name,
    };
  });
};

export const getDuplicateShortcutNameKey = (
  items: IUrlCollectionItem[],
  targetName: string,
  ignoreKey = '',
): string => {
  const normalizedTargetName = targetName.trim().toLowerCase();

  if (!normalizedTargetName) {
    return '';
  }

  const duplicateItem = items.find((item) => {
    const itemKey = getUrlCollectionItemKey(item);

    if (ignoreKey && itemKey === ignoreKey) {
      return false;
    }

    return item.name.trim().toLowerCase() === normalizedTargetName;
  });

  return duplicateItem ? getUrlCollectionItemKey(duplicateItem) : '';
};

export const getDuplicateShortcutUrlKey = (items: IUrlCollectionItem[], targetUrl: string, ignoreKey = ''): string => {
  const normalizedTargetUrl = getUrlCollectionComparableUrl(targetUrl);

  if (!normalizedTargetUrl) {
    return '';
  }

  const duplicateItem = items.find((item) => {
    const itemKey = getUrlCollectionItemKey(item);

    if (ignoreKey && itemKey === ignoreKey) {
      return false;
    }

    return getShortcutItemUrlKey(item) === normalizedTargetUrl;
  });

  return duplicateItem ? getUrlCollectionItemKey(duplicateItem) : '';
};

export const getShortcutItemsValidationError = (
  items: IUrlCollectionItem[],
): {
  key: string;
  type: '' | 'empty_name' | 'long_name' | 'duplicate_name' | 'duplicate_url';
} => {
  const nameSet = new Set<string>();
  const urlSet = new Set<string>();

  for (const item of items) {
    const itemKey = getUrlCollectionItemKey(item);
    const itemName = item.name.trim();
    const normalizedName = itemName.toLowerCase();
    const normalizedUrl = getShortcutItemUrlKey(item);

    if (!itemName) {
      return {
        key: itemKey,
        type: 'empty_name',
      };
    }

    if (itemName.length > SHORTCUT_NAME_MAX_LENGTH) {
      return {
        key: itemKey,
        type: 'long_name',
      };
    }

    if (nameSet.has(normalizedName)) {
      return {
        key: itemKey,
        type: 'duplicate_name',
      };
    }

    if (normalizedUrl && urlSet.has(normalizedUrl)) {
      return {
        key: itemKey,
        type: 'duplicate_url',
      };
    }

    nameSet.add(normalizedName);

    if (normalizedUrl) {
      urlSet.add(normalizedUrl);
    }
  }

  return {
    key: '',
    type: '',
  };
};
